// node modules
import dayjs from 'dayjs';
import set from 'lodash/set';
// modules
import AuthService from 'modules/Auth';
import UserStore from 'modules/User';
import BodService from 'modules/BOD';
import LoadingStore from 'modules/Loading';
import RoutingStore from 'modules/Routing';
import { LStorage, CacheStorageService } from 'modules/Storage';
import Logger from 'modules/Logger';
import LocalizationService from 'modules/Localization';
import { Logger as UIlibLogger } from '@upstox/upstox-ui';
import SpecialTradingDates from 'modules/SpecialTradingDates';
import FirebaseService from 'modules/Firebase';
import Fetch from 'modules/Fetch';
import NotificationCenter from 'modules/NotificationCenter';
import AnalyticsService from 'modules/Analytics/service';
import FeedbackService from 'modules/Feedback';
import MarketHolidayDates from 'modules/MarketHolidayDates';
import FingerprintJS from 'modules/FingerprintJS';
import PriceAlertsService from 'modules/PriceAlerts/service';
// config
import { ENV, VERSION } from 'config';
// utils
import { cleanupAndSetDefaultState } from 'utils/helpers/whatsNewUtils';
// typings
import { INotification } from 'modules/NotificationCenter/typings';
import { CounterWithExpiry } from 'modules/User/typings';
// constants
import { EVENTS } from 'modules/Analytics/consts';
import { DEFAULT_REMOTE_CONFIG, ReleaseNotesIndicatorStates, WhatsNewIndicatorConfig, WhatsNewIndicatorStatus } from './consts';
// dayjs config
import { dayjsPluginsConfig } from './dayjsConfig';
// store
import AppStore from './store';

class AppService extends AppStore {
  configName = {
    PROD: 'config.json',
    QA: 'config.json',
    UAT: 'config.json',
  } as const;

  config = DEFAULT_REMOTE_CONFIG;
  configRefreshInterval: ReturnType<typeof setInterval> | undefined;

  constructor() {
    super();

    const query = RoutingStore.getQuery() as AppQueryObject;
    this.setQueryParams(query);

    // eslint-disable-next-line no-console
    console.log(`ENV: ${ENV}, VERSION: ${VERSION}`);
  }

  // add all needed plugins to dayjs here
  private dayjsInit = () => {
    dayjsPluginsConfig.forEach(({ plugin, config }) => {
      if (config) {
        dayjs.extend(plugin, config);
      } else {
        dayjs.extend(plugin);
      }
    });

    dayjs.tz.setDefault('Asia/Calcutta');

    LocalizationService.initDayJsLocales();
  };

  init = async () => {
    // lead user check
    const leadUser = UserStore.checkLeadUsers();
    if (leadUser) return;
    // auth service init
    AuthService.init();

    // enable logger in UI lib
    UIlibLogger.setLoggerState(Logger.loggerEnabled);

    // init all dayjs plugins
    this.dayjsInit();

    // clear local storage keys if needed
    LStorage.checkKeysForClearing();

    // check vistor id
    const visitorId = LStorage.getItem('DEVICE_UUID_FINGER_PRINT_PRO');
    const isIncognito = LStorage.getItem('INCOGNITO_WINDOW');

    // if visitorId or incognito is not present, initialize fingerprint js
    if (!visitorId || !isIncognito) {
      await FingerprintJS.init();
    }

    // priority initializations
    BodService.bodParse();

    try {
      await UserStore.profileRequest();
      await CacheStorageService.init();
      await SpecialTradingDates.init();
      await MarketHolidayDates.init();
      await this.initAWSConfig();
      await UserStore.userPreferenceSettingsRequest();
      await PriceAlertsService.getPriceAlerts();
    } catch (error) {
      AnalyticsService.sendEvent(
        EVENTS.ERROR_OCCURRED,
        {
          error,
        },
        true,
      );
    }

    Logger.log('APP', 'App initialised');

    // initially it's true (check Loading module consts)
    LoadingStore.setLoading('APP_INIT', false);

    // feedback widget load
    FeedbackService.load();

    // Firebase Initialization
    await FirebaseService.init();

    this.loginNotification();

    // TODO: Uncomment this once chart sync release is successful
    // if (UserStore.userPreferenceSettings?.saveChartsToServer) {
    //   ChartSync.clearChartDataFromLocalStorage();
    // }

    this.addNetworkListeners();
  };

  private addNetworkListeners = () => {
    window.addEventListener('offline', this.handleNetworkError);
    window.addEventListener('online', this.handleTryReconnect);
  };

  private removeNetworkListeners = () => {
    window.removeEventListener('offline', this.handleNetworkError);
    window.removeEventListener('online', this.handleTryReconnect);
  };

  // cleanup method
  dispose = () => {
    this.removeNetworkListeners();

    if (this.configRefreshInterval) {
      clearInterval(this.configRefreshInterval);
      this.configRefreshInterval = undefined;
    }
  };

  // set global states based on the indicatorType from config & the local storage values
  updateReleaseNotesState = (
    whatsNewIndicatorConfig: WhatsNewIndicatorConfig,
  ) => {
    // If S3 config is not present or invalid, remove the local storage item
    // early return so fallback on default Release Notes state
    if (!whatsNewIndicatorConfig || !whatsNewIndicatorConfig.type) {
      LStorage.removeItem('WHATS_NEW_INDICATOR_STATUS');
      return;
    }

    // Cleanup & set default state in LS
    cleanupAndSetDefaultState(whatsNewIndicatorConfig);

    const whatsNewIndicatorStatus: WhatsNewIndicatorStatus | null =
      LStorage.getItem(
        'WHATS_NEW_INDICATOR_STATUS',
      );
    const isIntimationModalType = whatsNewIndicatorConfig.type === 'INTIMATION_MODAL';
    const isNewReleaseNotesType = whatsNewIndicatorConfig.type === 'NEW_RELEASE_NOTES';
    const { maxCount: configCount } = whatsNewIndicatorConfig;
    // Default state to regular release notes
    let indicatorState = ReleaseNotesIndicatorStates.RELEASE_NOTES;

    const currentTime = dayjs();
    const endOfDay = dayjs().endOf('day');
    const { count, expiry } = whatsNewIndicatorStatus || {
      count: 0,
      expiry: endOfDay.toJSON(),
    };
    const expiryTime = expiry ? dayjs(expiry) : endOfDay;
    if (isIntimationModalType && count === 0) {
      indicatorState = ReleaseNotesIndicatorStates.NEW_INTIMATION;
    } else if (
      isIntimationModalType &&
      count < configCount &&
      currentTime.isAfter(expiryTime)
    ) {
      indicatorState = ReleaseNotesIndicatorStates.INTIMATION;
    } else if (isNewReleaseNotesType && count === 0) {
      indicatorState = ReleaseNotesIndicatorStates.NEW_RELEASE_NOTES;
    }

    // Update the global release notes indicator state
    this.setReleaseNotesIndicatorState(indicatorState);
  };

  fetchConfig = async (): Promise<typeof DEFAULT_REMOTE_CONFIG> => {
    try {
      const response = await Fetch.request<'AWS_UPSTOX'>(
        'AWS_UPSTOX',
        `/web/trading-view/${this.configName[ENV]}`,
        'S3_CONFIG',
        {
          addRequestIdHeader: false,
        },
      );

      if (response) {
        const configResponse =
          (response as unknown) as typeof DEFAULT_REMOTE_CONFIG;
        this.updateReleaseNotesState(
          configResponse.releaseNotes.whatsNewIndicatorConfig,
        );
        return configResponse;
      }

      return DEFAULT_REMOTE_CONFIG;
    } catch (error) {
      return DEFAULT_REMOTE_CONFIG;
    }
  };

  initAWSConfig = async () => {
    const awsConfig = await this.fetchConfig();
    this.config = { ...this.config, ...awsConfig };

    // refresh config every 15 min
    const min15 = 1000 * 60 * 15;

    this.configRefreshInterval = setInterval(() => {
      this.fetchConfig().then(newAwsConfig => {
        this.config = { ...this.config, ...newAwsConfig };
      });
    }, min15);
  };

  loginNotification() {
    try {
      const loginCounter: CounterWithExpiry | null = LStorage.getItem(
        'LOGIN_COUNT',
      );

      const currentTime = dayjs();
      const endOfDay = dayjs().endOf('day');
      let { count, expiry } = loginCounter || {
        count: 0,
        expiry: endOfDay.toJSON(),
      };
      const expiryTime = expiry ? dayjs(expiry) : endOfDay;

      const loginNotification = this.config
        .sebi_compliance_modal as INotification;

      // check if storage value expired
      if (!loginCounter || currentTime.isAfter(expiryTime)) {
        expiry = endOfDay.toJSON();
        count = 0;
        LStorage.setItem('LOGIN_COUNT', { count, expiry });
      }

      count = Number.isNaN(count) ? 0 : count;

      if (!loginNotification) return;

      set(loginNotification, 'startDate', currentTime.toJSON());
      set(loginNotification, 'endDate', endOfDay.toJSON());

      if (count < 1) {
        NotificationCenter.push(loginNotification, loginNotification.variant);
      }
    } catch {
      // Do Nothing
    }
  }

  handleNetworkError = () => {
    this.setIsOffline(true);
  };

  handleTryReconnect = () => {
    if (navigator.onLine) {
      this.setIsOffline(false);
    }
  };
}

export default new AppService();
